import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RouteSelectedFund } from '@components/fondsportrait/selected-fund';
import { firstValueFrom } from 'rxjs';
import { manifestUrls } from 'src/environments/environment';
import { InterceptorSkipHeader } from './common/interceptors/auth.interceptor';

type Manifest = {
  webComponent: {
    attributes: { [key: string]: string };
    url: string;
    tag: string;
  };
};

@Injectable({ providedIn: 'root' })
export class Extern {
  private readonly http = inject(HttpClient);
  private readonly MANIFEST: string = 'manifestJSON';
  private readonly routeSelectedFund = inject(RouteSelectedFund);

  public async placeDoka(
    container: HTMLElement,
    loginToken: string,
    authenticatedUserId: unknown,
    opts?: Record<string, string>
  ): Promise<void> {
    const manifest = await this.fetchAndAddDokaAttributes(loginToken, authenticatedUserId);

    if (opts) {
      manifest.webComponent.attributes = {
        ...manifest.webComponent.attributes,
        ...opts,
      };
    }

    this.createElement(container, manifest);
  }

  public async placeFpSidepanelCreateSubscriptionButton(container: HTMLElement, funds: string): Promise<void> {
    const headers = new HttpHeaders().set(InterceptorSkipHeader, '');
    const manifest = await firstValueFrom(
      this.http.get<Manifest>(manifestUrls.fondstableSidepanel.addSubscription, { headers: headers })
    );
    manifest['webComponent']['attributes'] = { funds: funds };
    this.createElement(container, manifest);
  }

  public async placeFpSidepanelSubscribedDocumentsWidget(container: HTMLElement, funds: string): Promise<void> {
    const headers = new HttpHeaders().set(InterceptorSkipHeader, '');
    const manifest = await firstValueFrom(
      this.http.get<Manifest>(manifestUrls.fondstableSidepanel.subscribedDocuments, { headers: headers })
    );
    manifest['webComponent']['attributes'] = { funds: funds };
    this.createElement(container, manifest);
  }

  private async fetchAndAddDokaAttributes(loginToken: string, authenticatedUserId: unknown): Promise<any> {
    const manifest = await firstValueFrom(this.http.get<Manifest>(manifestUrls.doka));
    return this.addDokaAttributes(manifest, loginToken, authenticatedUserId);
  }

  private addDokaAttributes(manifest: Manifest, loginToken: string, authenticatedUserId: unknown): any {
    if (manifest && manifest.webComponent) {
      manifest.webComponent.attributes = {
        'user-id': authenticatedUserId as string,
        'login-token': loginToken,
        'hide-funds-filter': 'true',
        'use-url-params': 'false',
        'fund-number': this.routeSelectedFund.refresh().get(),
      };
    }
    return manifest;
  }

  private createElement(container: HTMLElement, manifest: Manifest): void {
    if (!document.querySelector(`script[src="${manifest['webComponent'].url}"]`)) {
      const script = document.createElement('script');
      script.src = manifest['webComponent'].url;
      script.type = 'module';
      script.async = true;
      document.body.appendChild(script);
    }

    const element: HTMLElement = document.createElement(manifest.webComponent.tag);
    const attributes = manifest.webComponent.attributes;
    for (let key in attributes) {
      if (attributes.hasOwnProperty(key)) {
        element.setAttribute(key, attributes[key]);
      }
    }

    container.appendChild(element);
  }
}
